import { useAppSelector } from 'app/hooks';
import CoverageShortfallTooltip from 'components/common/coverage-shortfall-tooltip';
import { SystemOptionType } from 'data/types';
import {
  getDuctedCoverage,
  getZoneDuctedCoverage
} from 'utils/ducted-coverage';
import {
  equipmentPlaceholderNote,
  isEquipmentPlaceholderProduct
} from 'utils/equipment-placeholder';
import { abbreviateNumber } from 'utils/helpers';
import {
  isPtacIndoorFace,
  isPtacSelectedSystem
} from 'utils/ptac-selected-system';
import { buildPtacSizeMatches } from 'utils/ptac-size-match';
import { MATCH_TEXT_CLASS } from 'utils/zone-match-styles';
import {
  ductedZoneCoverageCell,
  ductlessZoneCoverageCell
} from './zone-coverage-cell';

type ZonePerformanceTableProps = Readonly<{
  system: SystemOptionType;
}>;

// One line per unit in the "Selected Unit (Output)" cell: the zone's indoor
// unit (cooling output) and, when present, its blower/furnace (heating
// output). Placeholder units render their placeholder name + note; capacity
// 0 renders "—", never "0 BTU" (AHA-6796).
type UnitLine = {
  key: string;
  label: string;
  outputBTU: number;
  placeholderNote: string | null;
};

export default function ZonePerformanceTable({
  system
}: ZonePerformanceTableProps) {
  // Ducted design-load coverage (AHA-6796): ducted CCB systems are
  // single-condenser/single-zone, so the one row shows the system-level
  // design-load percentages ("—" for null) and the footnote explains the
  // metric. Ductless rows/footnote untouched.
  const ductedCoverage = getDuctedCoverage(system);
  // Hotel Style PTAC: one unit per room, so each row also gets the Size Match
  // the ducted detail shows — the unit's rated output against the room's
  // design load, classified against the admin sizing bands.
  const sizingBands = useAppSelector((state) => state.data.sizingBands);
  const isPtac = isPtacSelectedSystem(system);
  const sizeMatches = isPtac ? buildPtacSizeMatches(system, sizingBands) : [];

  const rows = system.configuration.condensers.flatMap((cond) => {
    const coverage = system.condenserCoverage.find(
      (c) => c.uniqueId === cond.uniqueId
    );
    const shortfallCoverage = coverage ? [coverage] : system.condenserCoverage;
    return cond.zones.map((zone) => {
      const sizeMatch = sizeMatches.find(
        (match) => match.zoneUniqueId === zone.uniqueId
      );
      // A PTAC's indoor face IS the unit, mirrored under the sentinel id
      // the kept-equipment placeholders use — name the unit, no kept note.
      const indoorIsPtacFace = isPtacIndoorFace(zone.selectedProduct);
      const units: UnitLine[] = [
        {
          key: 'indoor',
          label:
            indoorIsPtacFace ||
            isEquipmentPlaceholderProduct(zone.selectedProduct)
              ? zone.selectedProduct?.name ?? '—'
              : zone.selectedProduct?.type ?? '—',
          outputBTU: zone.selectedProduct?.coolingBTU ?? 0,
          placeholderNote: indoorIsPtacFace
            ? null
            : equipmentPlaceholderNote(zone.selectedProduct)
        }
      ];
      if (zone.blowerProduct) {
        units.push({
          key: 'blower',
          label: isEquipmentPlaceholderProduct(zone.blowerProduct)
            ? zone.blowerProduct.name
            : zone.blowerProduct.type,
          outputBTU: zone.blowerProduct.heatingBTU ?? 0,
          placeholderNote: equipmentPlaceholderNote(zone.blowerProduct)
        });
      }
      const zoneDuctedCoverage = ductedCoverage
        ? getZoneDuctedCoverage(zone) ?? ductedCoverage
        : null;
      return {
        zoneName: zone.name,
        units,
        sizeMatch: sizeMatch?.match ?? null,
        cooling: zoneDuctedCoverage
          ? ductedZoneCoverageCell(zoneDuctedCoverage.coolingCoveragePercent)
          : ductlessZoneCoverageCell(
              'cooling',
              coverage?.coolingScore ?? system.coolingScore,
              shortfallCoverage
            ),
        heating: zoneDuctedCoverage
          ? ductedZoneCoverageCell(zoneDuctedCoverage.heatingCoveragePercent)
          : ductlessZoneCoverageCell(
              'heating',
              coverage?.heatingScore ?? system.heatingScore,
              shortfallCoverage
            )
      };
    });
  });

  if (rows.length === 0) return null;

  return (
    <div>
      <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
        Zone Performance &amp; Size Match
      </div>
      <div className='overflow-hidden rounded-lg border border-gray-200'>
        <table className='w-full text-sm'>
          <thead className='border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500'>
            <tr>
              <th className='px-3 py-2 text-left'>Zone</th>
              <th className='px-3 py-2 text-left'>Selected Unit (Output)</th>
              {isPtac && <th className='px-3 py-2 text-left'>Size Match</th>}
              <th className='px-3 py-2 text-right'>Heating Coverage</th>
              <th className='px-3 py-2 text-right'>Cooling Coverage</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${row.zoneName}-${index}`}>
                <td className='whitespace-nowrap px-3 py-2 font-medium text-gray-900'>
                  {row.zoneName}
                </td>
                <td className='px-3 py-2 text-gray-700'>
                  {row.units.map((unit) => (
                    <div key={unit.key}>
                      {unit.outputBTU
                        ? `${abbreviateNumber(unit.outputBTU)} BTU `
                        : !unit.placeholderNote && '— '}
                      {unit.label}
                      {unit.placeholderNote && (
                        <span className='italic text-gray-400'>
                          {' '}
                          {unit.placeholderNote}
                        </span>
                      )}
                    </div>
                  ))}
                </td>
                {isPtac && (
                  <td className='px-3 py-2'>
                    <span
                      className={`font-medium ${
                        MATCH_TEXT_CLASS[row.sizeMatch?.variant ?? 'unknown']
                      }`}
                    >
                      {row.sizeMatch?.label ?? '—'}
                    </span>
                  </td>
                )}
                <td className='px-3 py-2 text-right text-gray-700'>
                  <CoverageShortfallTooltip
                    shortfalls={row.heating.shortfalls}
                    className='inline-block'
                  >
                    {row.heating.value}
                  </CoverageShortfallTooltip>
                </td>
                <td className='px-3 py-2 text-right text-gray-700'>
                  <CoverageShortfallTooltip
                    shortfalls={row.cooling.shortfalls}
                    className='inline-block'
                  >
                    {row.cooling.value}
                  </CoverageShortfallTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className='mt-2 text-xs text-gray-400'>
        {ductedCoverage &&
          "Coverage percentages compare the system's rated capacity to " +
            "your home's design heating and cooling loads, capped at 100%. " +
            '"—" means the coverage couldn\'t be calculated.'}
        {isPtac &&
          "Size Match compares each unit's rated output to its room's " +
            'design heating and cooling loads; coverage percentages are ' +
            'capped at 100%. "—" means the match couldn\'t be calculated.'}
        {!ductedCoverage &&
          !isPtac &&
          'Coverage percentages show how often the system can meet or ' +
            'exceed the heating and cooling demand in your area.'}
      </p>
    </div>
  );
}
